import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import {
  GroupUnitChip,
  NBSP,
  RateKindChip,
  ScrollRegion,
  VisuallyHidden,
  basisText,
  fleetLeading,
  fleetType,
  formatIsoDate,
  formatMoney,
  rateUnit,
  typo,
  type RateRow,
} from '@entities/fleet';

/**
 * Ставки групп на дату. Широкий контейнер — таблица; узкий — строки-карточки:
 * группа и ставка, ниже откуда она и с какой даты, ещё ниже — «Задать ставку».
 */

const NARROW = '640px';

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};

  thead th {
    padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderStrong};
    font-size: ${fleetType.micro};
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    text-align: left;
    color: ${({ theme }) => theme.colors.text.secondary};
    vertical-align: bottom;
  }

  thead th.num {
    text-align: right;
  }

  td {
    padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
    color: ${({ theme }) => theme.colors.text.primary};
    transition: background ${({ theme }) => theme.motion.fast}
      ${({ theme }) => theme.motion.easeOut};
  }

  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  td.c-name {
    min-width: 220px;
  }

  td.c-action {
    width: 1%;
    text-align: right;
    white-space: nowrap;
  }

  tr[data-missing='true'] > td {
    background: ${({ theme }) => theme.colors.status.danger}0A;
  }

  tr:hover > td,
  tr:focus-within > td {
    background: ${({ theme }) => theme.colors.surface};
  }

  .narrow-only {
    display: none;
  }

  @container rates (max-width: ${NARROW}) {
    &,
    & > tbody,
    & tr,
    & td {
      display: block;
    }

    & > thead,
    .wide-only {
      display: none;
    }

    .narrow-only {
      display: block;
    }

    tr {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      column-gap: ${({ theme }) => theme.spacing(3)};
      row-gap: ${({ theme }) => theme.spacing(2)};
      padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(2)}`};
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }

    tr > td {
      padding: 0;
      border: none;
      min-width: 0;
      background: none;
    }

    tr[data-missing='true'],
    tr:hover,
    tr:focus-within {
      background: ${({ theme }) => theme.colors.surface};
    }

    td.c-name {
      grid-column: 1;
      grid-row: 1;
    }

    td.c-rate {
      grid-column: 2;
      grid-row: 1;
    }

    td.c-meta {
      grid-column: 1 / -1;
      grid-row: 2;
    }

    td.c-action {
      grid-column: 1 / -1;
      grid-row: 3;
      width: auto;
      text-align: left;
    }

    td.c-action > button {
      width: 100%;
    }
  }
`;

const NameButton = styled.button`
  display: block;
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: ${fleetType.base};
  font-weight: 500;
  line-height: ${fleetLeading.tight};
  text-align: left;
  text-wrap: pretty;
  overflow-wrap: anywhere;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Sub = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Rate = styled.span`
  display: block;
  font-size: ${fleetType.base};
  font-weight: 600;

  &[data-none='true'] {
    font-weight: 400;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const Unit = styled.span`
  display: block;
  font-size: ${fleetType.micro};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Fallback = styled.span`
  display: block;
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

function since(row: RateRow): string {
  return row.state.kind === 'missing' ? '—' : formatIsoDate(row.state.validFrom);
}

function basis(row: RateRow): string {
  return row.state.kind === 'missing' ? '—' : basisText(row.state.basis);
}

interface RatesTableProps {
  rows: readonly RateRow[];
  canWrite: boolean;
  /** Объект — видна и ставка по умолчанию под своей; холдинг — без неё. */
  showFallback: boolean;
  onOpen: (groupId: string, view: 'new' | 'history') => void;
}

export function RatesTable({ rows, canWrite, showFallback, onOpen }: RatesTableProps) {
  return (
    <ScrollRegion label="Таблица ставок групп">
      <Table>
        <thead>
          <tr>
            <th scope="col">Группа</th>
            <th scope="col" className="num">
              {typo('Ставка без НДС')}
            </th>
            <th scope="col">Откуда</th>
            <th scope="col">База</th>
            <th scope="col">{`Действует${NBSP}с`}</th>
            {canWrite && (
              <th scope="col">
                <VisuallyHidden>Действие</VisuallyHidden>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const missing = row.state.kind === 'missing';
            const fallback =
              showFallback && row.state.kind === 'project' && row.fallback ? row.fallback : null;
            return (
              <tr key={row.groupId} data-missing={missing ? 'true' : undefined}>
                <td className="c-name">
                  <NameButton
                    type="button"
                    data-rate-open={row.groupId}
                    title="Ставка группы и история"
                    onClick={() => onOpen(row.groupId, canWrite && missing ? 'new' : 'history')}
                  >
                    {row.groupName}
                  </NameButton>
                  <Sub>
                    учёт: <GroupUnitChip unit={row.unit} />
                  </Sub>
                </td>
                <td className="num c-rate">
                  {row.state.kind === 'missing' ? (
                    <Rate data-none="true">—</Rate>
                  ) : (
                    <>
                      <Rate>{formatMoney(row.state.rate)}</Rate>
                      <Unit>{rateUnit(row.unit)}</Unit>
                    </>
                  )}
                </td>
                <td className="wide-only">
                  <RateKindChip kind={row.state.kind} />
                  {fallback && <Fallback>по умолчанию — {formatMoney(fallback.rate)}</Fallback>}
                </td>
                <td className="wide-only">{basis(row)}</td>
                <td className="wide-only">{since(row)}</td>
                <td className="narrow-only c-meta">
                  <Sub>
                    <RateKindChip kind={row.state.kind} />
                    {!missing && (
                      <span>
                        {basis(row)} · с {since(row)}
                      </span>
                    )}
                  </Sub>
                </td>
                {canWrite && (
                  <td className="c-action">
                    <Button
                      type="button"
                      $variant={missing ? 'primary' : 'secondary'}
                      $size="sm"
                      aria-label={`Задать ставку: ${row.groupName}`}
                      onClick={() => onOpen(row.groupId, 'new')}
                    >
                      Задать ставку
                    </Button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </ScrollRegion>
  );
}
